import { ReactNode, useEffect, useRef, useState } from 'react';
import { UseFormGetValues, UseFormSetValue, UseFormSubscribe } from 'react-hook-form';
import { TimecardDto } from 'types/timecards';
import { captureLanguageWarningShown, DescriptionVia } from 'utils/timecardAnalytics';
import { mismatchedDescriptionLanguage } from './descriptionLanguage';
import { DescriptionAiEdit, useDescriptionAiEdit } from './useDescriptionAiEdit';

/** How long the description has to go unchanged before its text is checked. */
const IDLE_CHECK_DELAY_MS = 1000;

type Props = {
    getValues: UseFormGetValues<TimecardDto>;
    /** Writes an AI edit into the description, and the original back on Undo. */
    setValue: UseFormSetValue<TimecardDto>;
    /** The form's `subscribe`, through which each change to the description is seen. */
    subscribe: UseFormSubscribe<TimecardDto>;
    timecardLanguageCode?: string | null;
    /** Where the description is written, as the warning's event says. */
    via: DescriptionVia;
    /**
     * Renders the description: told whether its text reads as another language than the timecard's, handed the
     * check to run at once (when the field loses focus, or the AI wrote the text), and the AI edits the field's AI
     * action writes through, so that one Undo serves them.
     */
    children: (isMismatch: boolean, checkDescription: () => void, aiEdit: DescriptionAiEdit) => ReactNode;
};

/**
 * Checks whether the description reads as the timecard language. The text is checked once it has gone a second
 * unchanged, whether typed or filled in from history or a template; at once when the field loses focus; and again
 * as it stands whenever the timecard language changes. A result holds until the next check. The warning is reported
 * as it appears, never for the text the form opened with.
 */
const DescriptionLanguageCheck = ({
    getValues,
    setValue,
    subscribe,
    timecardLanguageCode,
    via,
    children,
}: Props): JSX.Element => {
    const [isMismatch, setIsMismatch] = useState(false);
    // What the last check found; null until the first that read something, which reads the text the form opened with.
    const wasMismatch = useRef<boolean | null>(null);
    const [checkCount, setCheckCount] = useState(0);
    const aiEdit = useDescriptionAiEdit({ getValues, setValue, subscribe });

    // A run checks the text as it stands, then waits on it: each change restarts the idle delay, which ends in
    // the next run. Any next run (blur, a language change) drops the wait still pending, so the field's trim on
    // blur, itself a change, costs no second check.
    useEffect(() => {
        let isCurrent = true;
        const description = getValues('description');
        // A form applies its stored values after this first runs: an empty text with no case language is that blank
        // start, and seeds no baseline.
        const readNothing = !timecardLanguageCode && !description?.trim();
        void mismatchedDescriptionLanguage(description, timecardLanguageCode)
            // The check is advisory: a detector chunk that fails to load just shows no warning.
            .catch(() => null)
            .then((language) => {
                if (!isCurrent) {
                    // Overtaken while the detector loads, the first check still read the text the form opened with.
                    if (!readNothing) {
                        wasMismatch.current ??= language !== null;
                    }
                    return;
                }
                if (wasMismatch.current === false && language && timecardLanguageCode) {
                    captureLanguageWarningShown({ via, timecardLanguageCode, descriptionLanguage: language });
                }
                if (!readNothing) {
                    wasMismatch.current = language !== null;
                }
                setIsMismatch(language !== null);
            });
        let idleTimer: ReturnType<typeof setTimeout> | undefined;
        const unsubscribe = subscribe({
            name: 'description',
            formState: { values: true },
            callback: () => {
                clearTimeout(idleTimer);
                idleTimer = setTimeout(() => setCheckCount((count) => count + 1), IDLE_CHECK_DELAY_MS);
            },
        });
        return () => {
            isCurrent = false;
            unsubscribe();
            clearTimeout(idleTimer);
        };
    }, [checkCount, getValues, subscribe, timecardLanguageCode, via]);

    const checkDescription = () => setCheckCount((count) => count + 1);

    return <>{children(isMismatch, checkDescription, aiEdit)}</>;
};

export default DescriptionLanguageCheck;
